import { useState, useEffect, useMemo, useRef } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog.js";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Textarea } from "../../ui/textarea.js";

const REASONS: Array<{ value: string; label: string }> = [
  { value: "beta_feedback", label: "Beta feedback" },
  { value: "goodwill", label: "Goodwill" },
  { value: "internal", label: "Internal" },
  { value: "investor", label: "Investor" },
  { value: "marketing", label: "Marketing" },
];

interface WorkspaceLite {
  id: string;
  name: string;
  slug: string;
  firstUserEmail: string | null;
}

interface Props {
  isOpen: boolean;
  workspaces: WorkspaceLite[];
  initialWorkspaceId?: string | null;
  onClose: () => void;
  onCreated: (result: { balanceAfterCredits: number }) => void;
}

export function GrantCreditsModal({
  isOpen,
  workspaces,
  initialWorkspaceId,
  onClose,
  onCreated,
}: Props) {
  const [workspaceId, setWorkspaceId] = useState(initialWorkspaceId ?? "");
  const [credits, setCredits] = useState("");
  const [reason, setReason] = useState<string>("beta_feedback");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setWorkspaceId(initialWorkspaceId ?? (workspaces[0]?.id ?? ""));
      setCredits("");
      setReason("beta_feedback");
      setNote("");
      setError(null);
    }
  }, [isOpen, initialWorkspaceId, workspaces]);

  const creditsNum = Number(credits);
  const amountValid = Number.isFinite(creditsNum) && creditsNum > 0;

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/admin/credit-grants", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          workspaceId,
          credits: creditsNum,
          reason,
          note: note.trim() === "" ? null : note.trim(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? `Request failed (${res.status})`);
        return;
      }
      const body = (await res.json()) as { balanceAfterCredits: number };
      onCreated(body);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        aria-labelledby="grant-credits-title"
        className="w-[480px] max-w-full flex flex-col gap-3"
      >
        <DialogTitle id="grant-credits-title" className="text-base font-semibold">
          Grant credits
        </DialogTitle>

        <WorkspacePicker
          workspaces={workspaces}
          value={workspaceId}
          onChange={setWorkspaceId}
        />

        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Credits
          <Input
            type="number"
            step="1"
            min="1"
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            placeholder="10"
            inputSize="sm"
          />
          {amountValid && (
            <span className="text-[10px] text-text-tertiary">
              = ${creditsNum.toFixed(2)} USD
            </span>
          )}
        </label>

        <ReasonPicker value={reason} onChange={setReason} />

        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Note (optional)
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={1000}
          />
        </label>

        {error && (
          <div className="text-xs text-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={submitting || !amountValid || !workspaceId}
          >
            {submitting ? "Granting\u2026" : "Grant credits"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkspacePicker({
  workspaces,
  value,
  onChange,
}: {
  workspaces: WorkspaceLite[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = workspaces.find((w) => w.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        w.slug.toLowerCase().includes(q) ||
        (w.firstUserEmail ?? "").toLowerCase().includes(q),
    );
  }, [workspaces, query]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-1 text-xs relative"
      onKeyDown={handleKeyDown}
    >
      <span id="ws-picker-label" className="text-text-secondary">
        Workspace
      </span>
      <button
        type="button"
        aria-labelledby="ws-picker-label"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded px-2 py-1.5 text-sm flex items-center justify-between text-left gap-2 bg-[var(--bg-input)] border border-border text-text-primary"
      >
        <div className="flex flex-col min-w-0 flex-1">
          {selected ? (
            <>
              <span className="truncate">
                {selected.name}{" "}
                <span className="text-[10px] text-text-tertiary">
                  ({selected.slug})
                </span>
              </span>
              <span className="truncate text-[10px] text-text-tertiary">
                {selected.firstUserEmail ?? "no members"}
              </span>
            </>
          ) : (
            <span className="text-text-tertiary">Select a workspace…</span>
          )}
        </div>
        <ChevronDown size={14} className="text-text-tertiary" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-labelledby="ws-picker-label"
          className="absolute z-10 left-0 right-0 top-full mt-1 rounded shadow-lg flex flex-col bg-bg-base border border-border"
        >
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search size={12} className="text-text-tertiary" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, slug, or email…"
              aria-label="Search workspaces"
              className="flex-1 bg-transparent outline-none text-sm text-text-primary"
            />
          </div>
          <div className="overflow-y-auto max-h-64">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-text-tertiary">
                No matches.
              </div>
            ) : (
              filtered.map((w) => {
                const isSelected = w.id === value;
                return (
                  <button
                    key={w.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(w.id);
                      setOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 flex items-start gap-2 border-b border-border"
                    style={{
                      background: isSelected ? "rgba(35, 40, 60, 0.05)" : "transparent",
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate text-text-primary">
                        {w.name}{" "}
                        <span className="text-[10px] text-text-tertiary">
                          ({w.slug})
                        </span>
                      </div>
                      <div className="text-[10px] truncate text-text-tertiary">
                        {w.firstUserEmail ?? "no members"}
                      </div>
                    </div>
                    {isSelected && <Check size={12} className="text-accent" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReasonPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLabel = REASONS.find((r) => r.value === value)?.label ?? value;

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-1 text-xs relative"
      onKeyDown={handleKeyDown}
    >
      <span id="reason-picker-label" className="text-text-secondary">
        Reason
      </span>
      <button
        type="button"
        aria-labelledby="reason-picker-label"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded px-2 py-1.5 text-sm flex items-center justify-between text-left gap-2 bg-[var(--bg-input)] border border-border text-text-primary"
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={14} className="text-text-tertiary" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-labelledby="reason-picker-label"
          className="absolute z-10 left-0 right-0 top-full mt-1 rounded shadow-lg overflow-hidden bg-bg-base border border-border"
        >
          {REASONS.map((r) => {
            const isSelected = r.value === value;
            return (
              <button
                key={r.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(r.value);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm flex items-center justify-between text-text-primary border-b border-border"
                style={{
                  background: isSelected ? "rgba(35, 40, 60, 0.05)" : "transparent",
                }}
              >
                {r.label}
                {isSelected && <Check size={12} className="text-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
